import { IAPReward } from '../../../../Utility/RewardHelper';
import { IGameState } from '../../../../State/Game/IGameState';
import { Anim, StartAnimationClip } from '../../../Animation/AnimationHelper';
import TransformComponent from '../../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import Game from '../../../../Game';
import TextComponent from '../../../../Component/Render/Text';
import { IAPProduct } from '../../../../Utility/SDK/Drivers/IAPCatalog';
import GameObject, { GameObjectEvent } from '../../../../GameObject/GameObject';
import Armature from '../../../../Prototype/Armature';
import { GameActions } from '../../../../State/Game/GameActions';
import { ContextType } from '../../../../State/Game/ContextType';
import { PlayerActions } from '../../../../State/Player/PlayerActions';
import { DragonbonesDefault } from '../../../Animation/Clip';
import { parseCurrency } from '../../../../Utility/Currency';
import Renderable from '../../../../Component/Renderable';
import { report } from '../../../../report';
import { ProductId } from 'Utility/Product';

type FreeData = {
    freeLevelIcon: GameObject<PIXI.Sprite>;
    coinIcon: GameObject<PIXI.Sprite>;
    amountText: GameObject<PIXI.Text>;
};

type OldPrice = {
    text: GameObject<PIXI.Text>;
    strike: GameObject<PIXI.Text>;
};

type ShopPopupItemProps = {
    productId: ProductId;
    priceText: GameObject<PIXI.Text>;
    buyButton: GameObject<PIXI.Sprite>;
    product: IAPProduct;
    iapReward: IAPReward;
    freeData?: FreeData;
    dragonbones?: {
        name: string;
        parent: GameObject<PIXI.Container>;
    };
    oldPrice?: OldPrice;
};

export class ShopPopupItem {
    private readonly productId: ProductId;
    private readonly priceText: GameObject<PIXI.Text>;
    private readonly oldPrice?: OldPrice;
    private readonly buyButton: GameObject<PIXI.Sprite>;
    private readonly product: IAPProduct;
    private readonly iapReward: IAPReward;
    private readonly freeData?: FreeData;
    private readonly amount?: string;
    private freeIconVisible: boolean = false;

    constructor(props: ShopPopupItemProps) {
        this.productId = props.productId;
        this.priceText = props.priceText;
        this.oldPrice = props.oldPrice;
        this.buyButton = props.buyButton;
        this.product = props.product;
        this.iapReward = props.iapReward;
        this.freeData = props.freeData;

        this.priceText.getComponent(TextComponent).text = this.product.price;

        if (this.freeData) {
            this.amount = this.freeData.amountText.getComponent(TextComponent).text;
            this.freeIconVisible = Game.store.getState().playerState.inventory.shopGift > 0;
            this.freeData.freeLevelIcon.getComponent(TransformComponent).scale.set(this.freeIconVisible ? 1 : 0);
            this.freeData.coinIcon.getComponent(TransformComponent).scale.set(this.freeIconVisible ? 0 : 1);

            this.priceText.getComponent(TextComponent).text = this.freeIconVisible
                ? Game.locale.getText('Free')
                : this.product.price;

            this.freeData.amountText.getComponent(TextComponent).text = this.freeIconVisible ? 'X1' : this.amount;
        }

        if (props.dragonbones) {
            const armature = GameObject.Instantiate(Armature, {
                dragonbonesName: props.dragonbones.name,
                armatureName: 'Armature',
            });
            props.dragonbones.parent.addChild(armature);
            StartAnimationClip(new DragonbonesDefault(armature, { animationName: 'animtion0' }));
        }

        this.buyButton.on(GameObjectEvent.SELECTED, this.handleBuyClick);
        this.tryToSetOldPrice();
    }

    public handleStateChanged(state: IGameState): void {
        if (this.freeData) {
            if (state.playerState.inventory.shopGift > 0 && !this.freeIconVisible) {
                this.freeIconVisible = true;

                this.popIn(this.freeData.freeLevelIcon);
                this.popOut(this.freeData.coinIcon);
                this.changeText(this.freeData.amountText, 'X1');
                this.changeText(this.priceText, Game.locale.getText('Free'));
            }

            if (state.playerState.inventory.shopGift <= 0 && this.freeIconVisible) {
                this.freeIconVisible = false;

                this.popIn(this.freeData.coinIcon);
                this.popOut(this.freeData.freeLevelIcon);
                this.changeText(this.freeData.amountText, this.amount);
                this.changeText(this.priceText, this.product.price);
            }
        }
    }

    private popIn(object: GameObject): Promise<void> {
        return Anim(object, { scaleX: 1, scaleY: 1, duration: 500, easing: Easing.Back.Out });
    }

    private popOut(object: GameObject): Promise<void> {
        return Anim(object, { scaleX: 0, scaleY: 0, duration: 250 });
    }

    private async changeText(object: GameObject<PIXI.Text>, text: string): Promise<void> {
        await Anim(object, { alpha: 0, duration: 250 });
        object.getComponent(TextComponent).text = text;
        await Anim(object, { alpha: 1, duration: 250 });
    }

    private readonly handleBuyClick = (): void => {
        const state: IGameState = Game.store.getState();

        if (this.freeIconVisible && state.playerState.inventory.shopGift > 0) {
            PlayerActions.claimShopGift();
        } else {
            GameActions.setContext(ContextType.PURCHASE_PRODUCT, { product: this.iapReward });
        }
    };

    private tryToSetOldPrice(): void {
        if (!this.oldPrice) return;

        const parsedCurrency = parseCurrency(this.product.price);

        if (!parsedCurrency) {
            report('Failed to parse currency', {
                once: true,
                sampleRate: 0.1,
                tags: { localizedPrice: this.product.price },
            });
            this.oldPrice.text.getComponent(Renderable).visible = false;

            return;
        }

        const fullCost = parsedCurrency.value * (this.iapReward.usdPriceWithoutDiscount / this.iapReward.usdPrice);
        const language = Game.sdk.getLanguage();
        const fullCostStr = fullCost.toLocaleString(language, { maximumFractionDigits: 2 });

        if ((parseCurrency(fullCostStr)?.value ?? -Infinity) <= parsedCurrency.value) {
            report('Failed to convert full price', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    localizedPrice: this.product.price,
                    parsedPrice: parsedCurrency.value,
                    usdPriceWithoutDiscount: this.iapReward.usdPriceWithoutDiscount,
                    usdPrice: this.iapReward.usdPrice,
                    fullCostStr,
                    language,
                    productId: this.productId,
                },
                fingerprint: 'FailedToConvertFullPrice:tryToSetOldPrice',
            });
            this.oldPrice.text.getComponent(Renderable).visible = false;

            return;
        }

        this.oldPrice.text.getComponent(
            TextComponent
        ).text = `${parsedCurrency.beforeValue}${fullCostStr}${parsedCurrency.afterValue}`;

        this.oldPrice.strike.getComponent(TransformComponent).width = this.oldPrice.text.getComponent(
            TransformComponent
        ).width;
    }
}
